import { flatten } from "lodash";

import { OrderItem } from "~/common/types/order";
import { Address } from "~/common/types/return";
import { getCountryLabel } from "~/util/getCountryLabel";
import { transformCommaSeparatedStringToArray } from "~/util/transformCommaSeparatedStringToArray";

import {
  OrderDetailsResponse,
  OrderDetailsResponseRaw,
} from "./useOrderDetails.types";

export const transformOrderDetailsResponse = (
  responseData: OrderDetailsResponseRaw
): OrderDetailsResponse => {
  const order = responseData?.order?.[0];
  if (order) {
    const {
      id,
      order_name,
      order_number,
      customer = {},
      amount = "0",
      shipping_date,
      order_date,
      order_tags,
      payment_methods,
      return_labels = [],
      return_addresses = [],
      note_attributes,
      shipping_method,
      shipping_cost,
      no_of_items,
      invoice_number,
      more,
      registered_items_quantity,
      order_registered_returns,
      is_paid_via_gift_card,
      is_nested_order,
      shop_now_eligible,
    } = order;
    const { tags, email, address = {} } = customer;
    const {
      country_iso2,
      city,
      street,
      street_number,
      zip,
      street_2,
      province,
      province_code,
      company,
      phone,
    } = address;
    return {
      order: {
        id: id,
        orderName: order_name,
        orderNumber: order_number,
        more,
        returnLabels: return_labels.map(
          ({ qrcode_url, label_url, commercial_invoice_url }) => ({
            QRCodeUrl: qrcode_url,
            labelUrl: label_url,
            commercialInvoiceUrl: commercial_invoice_url,
          })
        ),
        amount: parseFloat(amount),
        orderDate: order_date,
        shippingDate: shipping_date,
        orderTags: order_tags,
        paymentMethods: payment_methods,
        customer: {
          tags,
          email,
          firstName: address.first_name,
          lastName: address.last_name,
          address: {
            city,
            street,
            street2: street_2,
            streetNumber: street_number,
            zip: zip,
            countryCode: country_iso2,
            province,
            provinceCode: province_code,
            company,
            phone,
          },
        },
        senderCustomer: {
          firstName: customer.first_name,
          lastName: customer.last_name,
        },
        noteAttributes: note_attributes,
        shippingMethod: shipping_method,
        shippingCost: parseFloat(shipping_cost),
        noOfItems: no_of_items,
        invoiceNumber: invoice_number,
        registeredItemsQuantity: registered_items_quantity,
        noOfRegisteredReturns: order_registered_returns,
        isPaidViaGiftCard: is_paid_via_gift_card,
        returnAddresses: return_addresses.map(
          ({
            return_address_city,
            return_address_state,
            return_address_company,
            return_address_country_iso2,
            return_address_last_name,
            return_address_street,
            return_address_street_no,
            return_address_zip_code,
          }) => ({
            companyName: return_address_company,
            city: return_address_city,
            state: return_address_state,
            countryCode: return_address_country_iso2,
            street: return_address_street,
            streetNumber: return_address_street_no,
            zip: return_address_zip_code,
            name: return_address_last_name,
          })
        ),
        nestedOrder: is_nested_order,
        shopNowEligible: shop_now_eligible,
      },
    };
  }
  return {
    order: {
      returnLabels: [],
      returnAddresses: [],
      customer: {
        address: {},
      },
      noteAttributes: [],
    },
  };
};

export const getReturnAddressAsText = ({
  street,
  streetNumber = "",
  zip,
  city,
  countryCode,
}: Partial<Address>) => {
  return `${street} ${streetNumber}, ${zip} ${city} ${getCountryLabel(
    countryCode
  )}`;
};

const ensureNonNegativePrice = (price: number) => {
  if (Math.sign(price) === -1) {
    return 0;
  }
  return price;
};

export const getOrderItemsFromOrderDetailsResponse = (
  pagesResponseData: OrderDetailsResponseRaw[] = []
): OrderItem[] => {
  return flatten(
    pagesResponseData.map((pageResponseData) => {
      const order = pageResponseData.order?.[0];
      if (order) {
        const orderItems = order.order_items ?? [];
        return orderItems.map(
          ({
            id,
            quantity,
            image_url,
            name,
            details1,
            details2,
            details,
            additional_comments,
            product_custom_data,
            price,
            refundable_tax,
            eligible,
            refund_eligible,
            exchange_eligible,
            custom_eligible,
            inventory_item_id,
            weight_in_kg,
            displayed_price,
            displayed_currency,
            sku,
            variant_id,
            product_id,
            ean,
            category,
            discount,
            original_quantity,
          }) => ({
            id,
            quantity,
            name,
            details1,
            details2,
            details,
            additionalComments: additional_comments,
            productCustomData: product_custom_data,
            imageUrl: image_url,
            price: ensureNonNegativePrice(price),
            displayedPrice: ensureNonNegativePrice(displayed_price),
            refundableTax: ensureNonNegativePrice(refundable_tax),
            eligible,
            refundEligible: refund_eligible,
            exchangeEligible: exchange_eligible,
            customEligible: custom_eligible,
            inventoryItemId: inventory_item_id,
            weightInKg: weight_in_kg,
            displayedCurrency: displayed_currency,
            sku,
            variantId: variant_id,
            productId: product_id,
            ean,
            categories: transformCommaSeparatedStringToArray(category),
            discount,
            originalQuantity: original_quantity,
          })
        );
      }
      return [];
    })
  );
};
